import React from 'react';
import {
  Box, List, ListItemButton, ListItemText, Typography, Chip, Divider,
  Tooltip, IconButton, CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { CATEGORY_META } from './categoryMeta';
import type { ExceptionCategory, ExceptionSummary } from './ExceptionAnalysis.api';

interface CategoryRailProps {
  summary: ExceptionSummary | null;
  loadingSummary: boolean;
  selected: ExceptionCategory;
  onSelect: (category: ExceptionCategory) => void;
  onRefresh: () => void;
}

const CategoryRail: React.FC<CategoryRailProps> = ({
  summary, loadingSummary, selected, onSelect, onRefresh,
}) => {
  const total = summary
    ? Object.values(summary).reduce((sum, n) => sum + (n || 0), 0)
    : 0;

  return (
    <Box sx={{ width: 300, flexShrink: 0, borderRight: 1, borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReportProblemOutlinedIcon color="warning" fontSize="small" />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>Categories</Typography>
        <Tooltip title="Refresh counts">
          <IconButton size="small" onClick={onRefresh} disabled={loadingSummary}>
            {loadingSummary ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <List dense sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        {CATEGORY_META.map(cat => {
          const count = summary?.[cat.slug] ?? null;
          const isSelected = selected === cat.slug;
          return (
            <ListItemButton
              key={cat.slug}
              selected={isSelected}
              onClick={() => onSelect(cat.slug)}
              sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: isSelected ? 600 : 400 }}>
                      {cat.label}
                    </Typography>
                    {count === null ? (
                      loadingSummary && <CircularProgress size={12} />
                    ) : (
                      <Chip
                        size="small"
                        label={count}
                        color={count === 0 ? 'default' : count > 100 ? 'error' : count > 20 ? 'warning' : 'default'}
                        sx={{ height: 20, fontVariantNumeric: 'tabular-nums' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', lineHeight: 1.3, mt: 0.5 }}>
                    {cat.detects}
                  </Typography>
                }
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">Total exceptions</Typography>
        <Chip
          size="small"
          label={loadingSummary ? '…' : total}
          color={total === 0 ? 'success' : total > 500 ? 'error' : 'default'}
          sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}
        />
      </Box>
    </Box>
  );
};

export default CategoryRail;
